/**
 * World 2: Many Things 🌈 (ages 6+). Lots of parts on one battery.
 * Under the hood: series vs. parallel. On screen: "in a row" vs. "their own road".
 *
 * Most levels use RAILS: a + road along the top and a − road along the bottom, made of
 * short wires that meet at junction snaps. Each thing the kid adds stands between the rails.
 * Rails are ordered "far segments first, bridges last" so stacks stay low.
 */
import type { Level, PresetPart } from './types';

const battery: PresetPart = { type: 'battery', x: 1, y: 1, rot: 1, locked: true };
const W = (x: number, y: number, rot: 0 | 1, length: number, color = 'purple'): PresetPart => ({
  type: 'wire',
  x,
  y,
  rot,
  props: { length, color },
  locked: true,
});
const free = (p: PresetPart): PresetPart => ({ ...p, locked: false });
const up = (type: PresetPart['type'], x: number, props = {}): PresetPart => ({
  type,
  x,
  y: 1,
  rot: 1,
  props,
});
const link = (x: number, y = 3) => free(W(x, y, 1, 2, 'blue'));
const WIRE = (count: number) => ({ type: 'wire' as const, props: { color: 'blue' }, count });

/** Rails with junctions at x = 4 and 7 (top row 1, bottom row 4). */
const rails47: PresetPart[] = [battery, W(4, 1, 0, 4), W(1, 1, 0, 4), W(4, 4, 0, 4), W(1, 4, 0, 4)];

export const WORLD2: Level[] = [
  {
    id: 'w2-1',
    world: 2,
    num: 1,
    title: 'Two in a Row',
    emoji: '🔅',
    intro: 'Can one battery light TWO bulbs? Put both bulbs in one loop, one after the other.',
    board: { cols: 8, rows: 6 },
    preset: [battery, W(1, 4, 0, 6)],
    tray: [{ type: 'lamp', count: 2 }, WIRE(4)],
    steps: [{ text: 'Light up both bulbs', when: { lit: 'lamp', count: 2, min: 0.12 } }],
    hints: [
      'Electricity should go from the battery, through the first bulb, then through the second bulb, and home.',
      'Put one bulb on the top road and one on the right side. Then draw wires to join them.',
      'Follow the glowing outlines, from snap to snap.',
    ],
    solution: (
      [
        { type: 'lamp', x: 2, y: 1, rot: 0 },
        up('lamp', 6),
        W(1, 1, 0, 2, 'blue'),
        W(4, 1, 0, 3, 'blue'),
        link(6),
      ] as PresetPart[]
    ).map(free),
    predict: {
      question: 'One bulb glows bright. If TWO bulbs share one loop, will they be…',
      choices: [
        { id: 'brighter', text: 'Brighter', emoji: '🌟' },
        { id: 'dimmer', text: 'Dimmer', emoji: '🕯️' },
        { id: 'same', text: 'The same', emoji: '🟰' },
      ],
      answer: 'dimmer',
      reveal: 'Two bulbs in a row share the battery’s push, so each one gets less and glows dimmer.',
    },
    outro:
      'Bulbs in a row share the push from the battery. Old Christmas lights were wired like this. If one bulb broke, they ALL went dark!',
  },
  {
    id: 'w2-2',
    world: 2,
    num: 2,
    title: 'Side by Side',
    emoji: '🔆',
    intro: 'This time, give each bulb its OWN road between the top rail and the bottom rail.',
    board: { cols: 9, rows: 6 },
    preset: rails47,
    tray: [{ type: 'lamp', count: 2 }, WIRE(3)],
    steps: [{ text: 'Make both bulbs shine bright', when: { lit: 'lamp', count: 2, min: 0.6 } }],
    hints: [
      'The top rail is the + road, the bottom rail is the − road. Stand each bulb between them.',
      'Put a bulb on a silver snap of the top rail, then draw a short wire down to the bottom rail.',
      'Look for the glowing outlines!',
    ],
    solution: [up('lamp', 4), up('lamp', 7), link(4), link(7)],
    predict: {
      question: 'In a row, two bulbs were dim. If each bulb gets its OWN road, will they be…',
      choices: [
        { id: 'brighter', text: 'Brighter', emoji: '🌟' },
        { id: 'dimmer', text: 'Dimmer', emoji: '🕯️' },
        { id: 'same', text: 'Still dim', emoji: '🟰' },
      ],
      answer: 'brighter',
      reveal:
        'With their own roads, each bulb gets the battery’s full push. Look at the sparks: two separate streams!',
    },
    outro:
      'Your house is wired like this! Every lamp has its own road, so one broken bulb never turns off the others.',
  },
  {
    id: 'w2-3',
    world: 2,
    num: 3,
    title: 'Light & Fan',
    emoji: '🏠',
    intro: 'It’s a hot night! Build a bedroom with a light AND a fan running at the same time.',
    board: { cols: 9, rows: 6 },
    preset: rails47,
    tray: [{ type: 'lamp', count: 1 }, { type: 'motor', count: 1 }, WIRE(3)],
    steps: [{ text: 'Light on AND fan spinning', when: { all: [{ lit: 'lamp' }, { spinning: true }] } }],
    hints: [
      'Give the bulb and the fan each their own road between the rails.',
      'Stand them on the top rail’s silver snaps, then draw wires down to the bottom rail.',
      'Follow the glowing outlines!',
    ],
    solution: [up('lamp', 4), up('motor', 7), link(4), link(7)],
    outro: 'One battery can run lots of different things at once, as long as each one has its own road.',
  },
  {
    id: 'w2-4',
    world: 2,
    num: 4,
    title: 'Two Switches',
    emoji: '🎛️',
    intro: 'Two rooms, two lights. Give each bulb its own switch, so you can turn them on one at a time.',
    board: { cols: 9, rows: 7 },
    preset: [battery, W(1, 4, 1, 2), W(4, 1, 0, 4), W(1, 1, 0, 4), W(4, 5, 0, 4), W(1, 5, 0, 4)],
    tray: [{ type: 'lamp', count: 2 }, { type: 'switch', count: 2 }, WIRE(2)],
    steps: [
      { text: 'Turn on just ONE bulb', when: { lit: 'lamp', count: 1, exactly: true } },
      { text: 'Now light BOTH bulbs', when: { lit: 'lamp', count: 2 } },
      { text: 'Turn them both off', when: { all: [{ has: 'switch', count: 2 }, { dark: 'lamp' }] } },
    ],
    hints: [
      'Each road needs a bulb AND a switch, one under the other.',
      'Stand a bulb on the top rail. Put a switch under it, reaching down to the bottom rail.',
      'Tap a switch to flip it. Each one controls only its own bulb!',
    ],
    solution: [
      up('lamp', 4),
      up('lamp', 7),
      { type: 'switch', x: 4, y: 3, rot: 1 },
      { type: 'switch', x: 7, y: 3, rot: 1 },
    ],
    demo: [
      { switch: 0, on: true },
      { wait: 0.5 },
      { switch: 1, on: true },
      { wait: 0.5 },
      { switch: 0, on: false },
      { switch: 1, on: false },
      { wait: 0.5 },
    ],
    outro:
      'Every room in your home has its own switch on its own road. That’s why turning off the kitchen light doesn’t turn off the TV.',
  },
  {
    id: 'w2-5',
    world: 2,
    num: 5,
    title: 'Color Party',
    emoji: '🌈',
    intro: 'Party time! Make the red, green and blue lights all glow together.',
    board: { cols: 9, rows: 6 },
    preset: [
      battery,
      W(1, 1, 0, 3),
      W(5, 1, 0, 3),
      W(3, 1, 0, 3),
      W(1, 4, 0, 3),
      W(5, 4, 0, 3),
      W(3, 4, 0, 3),
    ],
    tray: [
      { type: 'led', props: { color: 'red' }, count: 1 },
      { type: 'led', props: { color: 'green' }, count: 1 },
      { type: 'led', props: { color: 'blue' }, count: 1 },
      WIRE(4),
    ],
    steps: [{ text: 'Light all three colors', when: { lit: 'led', count: 3, min: 0.2 } }],
    hints: [
      'Give each light its own road between the top rail and the bottom rail.',
      'Stand each LED on a silver snap of the top rail, with its + at the top. Then draw a wire down.',
      'If an LED stays dark, it might be upside down. Tap it and press ⇄ to flip it!',
    ],
    solution: [
      up('led', 3, { color: 'red' }),
      up('led', 5, { color: 'green' }),
      up('led', 7, { color: 'blue' }),
      link(3),
      link(5),
      link(7),
    ],
    outro:
      'Red, green and blue are magic colors: mix them and you can make ANY color. Every pixel on a TV or phone screen is tiny red, green and blue lights!',
  },
  {
    id: 'w2-6',
    world: 2,
    num: 6,
    title: 'Treasure Lock',
    emoji: '🗝️',
    intro: 'The treasure light has a secret lock. Put two key switches in a row on the top road.',
    board: { cols: 8, rows: 6 },
    preset: [
      battery,
      { type: 'lamp', x: 6, y: 1, rot: 1, locked: true },
      W(1, 4, 0, 6),
      W(6, 3, 1, 2, 'blue'),
      W(1, 1, 0, 2, 'blue'),
    ],
    tray: [{ type: 'switch', count: 2 }],
    steps: [
      {
        text: 'Turn on BOTH keys to light the treasure',
        when: { all: [{ switchesOn: 2 }, { lit: 'lamp' }] },
      },
      { text: 'Now turn just one key off', when: { all: [{ switchesOn: 1 }, { dark: 'lamp' }] } },
    ],
    hints: [
      'There’s a gap on the top road just big enough for two switches.',
      'Put one switch right after the other, so the road goes through both.',
      'Tap both switches ON. Then tap one OFF and watch!',
    ],
    solution: [
      { type: 'switch', x: 2, y: 1, rot: 0 },
      { type: 'switch', x: 4, y: 1, rot: 0 },
    ],
    predict: {
      question: 'The treasure light has TWO key switches in a row. Will turning on just ONE key light it?',
      choices: [
        { id: 'yes', text: 'Yes, one is enough', emoji: '👍' },
        { id: 'no', text: 'No, it needs both', emoji: '🔐' },
      ],
      answer: 'no',
      reveal:
        'Both keys are on the same road. If either one is open, electricity can’t get through. Computers use this trick all the time. It’s called AND!',
    },
    demo: [
      { switch: 0, on: true },
      { wait: 0.3 },
      { switch: 1, on: true },
      { wait: 0.6 },
      { switch: 0, on: false },
      { wait: 0.5 },
    ],
    outro:
      'Two switches in a row is called an AND gate: this AND that. Real safes and rocket launch buttons use the same idea!',
  },
  {
    id: 'w2-7',
    world: 2,
    num: 7,
    title: 'Party Machine',
    emoji: '🎉',
    intro: 'The big finale! Build a party machine: one switch turns on a light, a fan AND a buzzer.',
    board: { cols: 12, rows: 6 },
    preset: [
      battery,
      W(1, 1, 0, 2, 'blue'),
      W(4, 1, 0, 3),
      W(8, 1, 0, 3),
      W(6, 1, 0, 3),
      W(1, 4, 0, 6),
      W(8, 4, 0, 3),
      W(6, 4, 0, 3),
    ],
    tray: [
      { type: 'switch', count: 1 },
      { type: 'lamp', count: 1 },
      { type: 'motor', count: 1 },
      { type: 'buzzer', count: 1 },
      WIRE(4),
    ],
    steps: [
      {
        text: 'Switch ON the whole party',
        when: { all: [{ switch: 'on' }, { lit: 'lamp' }, { spinning: true }, { buzzing: true }] },
      },
      { text: 'Switch it all OFF', when: { all: [{ switch: 'off' }, { dark: 'lamp' }, { quiet: true }] } },
    ],
    hints: [
      'The main switch goes in the gap next to the battery. Everything else gets its own road between the rails.',
      'Stand the bulb, the fan and the buzzer on the top rail’s silver snaps, then draw wires down.',
      'One switch on the shared road controls them all. Flip it!',
    ],
    solution: [
      { type: 'switch', x: 2, y: 1, rot: 0 },
      up('lamp', 6),
      up('motor', 8),
      up('buzzer', 10),
      link(6),
      link(8),
      link(10),
    ],
    outro:
      'A switch on the shared road is a MAIN switch. Houses have one too, in the fuse box, so grown-ups can turn off everything at once to stay safe.',
  },
];
